/**
 * Lead attribution: where a visitor first came from, sent with every lead form so the
 * super-admin Leads inbox can show source / country / language per lead.
 *
 * First touch = the UTMs, ad click ids, external referrer and landing page of the visitor's
 * first visit, kept for 90 days. A later visit only replaces it when the stored one carries no
 * campaign at all (plain direct/referral) and the new one does, so a QR code or ad click after an
 * organic visit still gets credit. Country is NOT collected here; the backend reads it from
 * Cloudflare.
 *
 * Storage: localStorage, except for visitors who need cookie consent (EU/UK, see tzNeedsConsent
 * in index.html) and haven't granted it, where it lives in sessionStorage for the visit only.
 * The data only ever leaves the browser inside a form the visitor chooses to submit, or on a link
 * the visitor clicks into the coach app (`tz_ft`, see tagAppLinksWithFirstTouch).
 */
import { splitLocale } from '../i18n/siteLocales';

const KEY = 'tz_first_touch';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;
const OWN_HOSTS = /(^|\.)(trainzilla\.(app|in)|tzilla\.live)$/;

type FirstTouch = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  landingPage?: string;
  firstSeenAt: string;
};

/** Matches LeadAttributionInput on tzilla-be. */
export type LeadAttribution = Omit<FirstTouch, 'firstSeenAt'> & {
  firstSeenAt?: string;
  submitPage?: string;
  locale?: string;
  timezone?: string;
};

const PARAMS: [keyof FirstTouch, string][] = [
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
  ['utmTerm', 'utm_term'],
  ['utmContent', 'utm_content'],
  ['gclid', 'gclid'],
  ['fbclid', 'fbclid'],
];

function canPersist(): boolean {
  const w = window as any;
  return !w.tzNeedsConsent || w.tzConsent === 'granted';
}

function read(): FirstTouch | null {
  for (const store of [() => localStorage, () => sessionStorage]) {
    try {
      const raw = store().getItem(KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as FirstTouch;
      if (Date.now() - Date.parse(parsed.firstSeenAt) < TTL_MS) return parsed;
    } catch {
      // storage blocked or bad JSON: treat as no first touch
    }
  }
  return null;
}

function write(touch: FirstTouch): void {
  try {
    (canPersist() ? localStorage : sessionStorage).setItem(KEY, JSON.stringify(touch));
  } catch {
    // private mode / blocked storage: attribution for this visit is just lost
  }
}

function hasCampaign(t: Partial<FirstTouch>): boolean {
  return Boolean(t.utmSource || t.utmMedium || t.utmCampaign || t.gclid || t.fbclid);
}

/** Record this page view as the first touch if it should be. Call once on app start. */
export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const touch: FirstTouch = { firstSeenAt: new Date().toISOString() };
  for (const [key, param] of PARAMS) {
    const value = url.searchParams.get(param)?.trim();
    if (value) (touch as any)[key] = value.slice(0, 300);
  }
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    if (ref && !OWN_HOSTS.test(ref.hostname)) touch.referrer = ref.origin + ref.pathname;
  } catch {
    // unparseable referrer
  }
  touch.landingPage = (url.pathname + url.search).slice(0, 300);

  const existing = read();
  if (!existing || (!hasCampaign(existing) && hasCampaign(touch))) {
    write(touch);
  } else if (canPersist()) {
    // Consent granted since the first touch was kept for the session: keep it for 90 days now.
    write(existing);
  }
}

/** Everything a lead form sends as `attribution`. */
export function getLeadAttribution(): LeadAttribution {
  if (typeof window === 'undefined') return {};
  const { locale } = splitLocale(window.location.pathname);
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }
  return {
    ...(read() ?? {}),
    submitPage: (window.location.pathname + window.location.search).slice(0, 300),
    locale,
    timezone,
  };
}

// ── Carrying the first touch into the coach app ─────────────────────────────
// app.trainzilla.in saves it on the coach account at sign-up (tzilla-be signupAttribution), so
// paid coaches can be attributed to a channel without GA4. It goes as our own `tz_ft` param, NOT
// as UTMs: UTMs between our GA-linked hosts would start a new GA session (see utils/outbound.ts).

const APP_HOST = 'app.trainzilla.in';
const APP_PARAM = 'tz_ft';

function base64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The `tz_ft` value for an app link, or null when there's no first touch to carry. */
export function firstTouchParam(): string | null {
  if (typeof window === 'undefined') return null;
  const touch = read();
  if (!touch) return null;
  const { locale } = splitLocale(window.location.pathname);
  // np = "don't persist": the visitor hasn't consented to storage, so the app keeps it per visit.
  return base64Url(JSON.stringify({ ...touch, locale, ...(canPersist() ? {} : { np: 1 }) }));
}

/** Add `tz_ft` to a coach-app URL (other URLs come back unchanged). */
export function withFirstTouch(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    if (u.hostname !== APP_HOST) return url;
    const value = firstTouchParam();
    if (!value) return url;
    u.searchParams.set(APP_PARAM, value);
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Stamp `tz_ft` onto app.trainzilla.in links as they are clicked (left, middle or right click),
 * so every CTA (including CMS blocks) carries it without each one opting in and the prerendered
 * HTML stays untagged. Call once on app start.
 */
export function tagAppLinksWithFirstTouch(): void {
  if (typeof document === 'undefined') return;
  const tag = (event: Event) => {
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor || anchor.hostname !== APP_HOST) return;
    const next = withFirstTouch(anchor.href);
    if (next !== anchor.href) anchor.href = next;
  };
  for (const type of ['click', 'auxclick', 'contextmenu']) {
    document.addEventListener(type, tag, { capture: true });
  }
}
